/**
 * What an SBOM upload onto a product or a product instance will do (DEV-107),
 * shown above the review. For an instance whose scanned configuration deviates
 * from every known version, the operator chooses here between a custom
 * version and a new official version.
 */

import { plural, t } from '@lingui/core/macro';
import { Trans, useLingui } from '@lingui/react/macro';
import { useEffect, useId, useState, type ReactNode } from 'react';

import { Banner } from '../../components/Banner';
import type {
  InstanceImportTarget,
  InstanceVersionChoice,
  ProductImportTarget,
} from '../../lib/types';

/** "Version 1.2" / "Version 1.2 (Kundenversion)"; call at render time. */
function versionName(versionNr: string, custom: boolean): string {
  return custom ? t`Version ${versionNr} (Kundenversion)` : t`Version ${versionNr}`;
}

function ProductSummary({ target }: { target: ProductImportTarget }): ReactNode {
  const { t } = useLingui();
  const affected = target.mode === 'update' ? target.affected_instances : 0;
  const versionNr = target.version.version_nr;
  const productName = target.product.name;
  const equalVersions = target.equals_versions.map((v) => versionName(v.version_nr, v.custom)).join(', ');
  return (
    <>
      <Banner
        kind={affected > 0 ? 'warn' : 'info'}
        sub={
          affected > 0
            ? t`${plural(affected, {
                one: 'Die geänderte Stückliste gilt für # Produktinstanz, die auf dieser Version läuft.',
                other: 'Die geänderte Stückliste gilt für # Produktinstanzen, die auf dieser Version laufen.',
              })}`
            : undefined
        }
      >
        {target.mode === 'new'
          ? <Trans>Neue offizielle Version <b>{versionNr}</b> von <b>{productName}</b> wird angelegt.</Trans>
          : <Trans>Version <b>{versionNr}</b> von <b>{productName}</b> wird aktualisiert.</Trans>}
      </Banner>
      {target.equals_versions.length > 0 ? (
        <Banner kind="warn" sub={t`Prüfen Sie, ob Sie wirklich eine weitere Version mit gleicher Stückliste brauchen.`}>
          <Trans>Die gescannte Konfiguration entspricht bereits {equalVersions}.</Trans>
        </Banner>
      ) : null}
    </>
  );
}

interface InstanceSummaryProps {
  target: InstanceImportTarget;
  disabled: boolean;
  onChoice: (choice: InstanceVersionChoice | null) => void;
}

function InstanceSummary({ target, disabled, onChoice }: InstanceSummaryProps): ReactNode {
  const { t } = useLingui();
  const versions = target.official_versions;
  const idPrefix = useId();
  const [mode, setMode] = useState<'custom' | 'official'>('custom');
  const [versionNr, setVersionNr] = useState('');
  const [predecessorId, setPredecessorId] = useState<number | null>(target.base_version.id);

  const taken = new Set(versions.map((v) => v.version_nr.trim().toLowerCase()));
  const trimmed = versionNr.trim();
  const duplicate = trimmed !== '' && taken.has(trimmed.toLowerCase());

  useEffect(() => {
    if (target.outcome !== 'deviation') {
      onChoice(null);
    } else if (mode === 'custom') {
      onChoice({ mode: 'custom' });
    } else if (trimmed !== '' && !duplicate) {
      onChoice({ mode: 'official', version_nr: trimmed, predecessor_id: predecessorId ?? undefined });
    } else {
      onChoice(null);
    }
  }, [target.outcome, mode, trimmed, duplicate, predecessorId, onChoice]);

  const instance = `${target.product.name} ${target.instance.serial}`.trim();
  const current = versionName(target.current_version.version_nr, target.current_version.custom);
  const { changed, added, missing } = target.counts;
  const differenceList = [
    changed > 0 ? t`${plural(changed, { one: '# geänderte Komponente', other: '# geänderte Komponenten' })}` : null,
    added > 0 ? t`${plural(added, { one: '# neue Komponente', other: '# neue Komponenten' })}` : null,
    missing > 0
      ? t`${plural(missing, {
          one: '# Komponente fehlt im Scan (bleibt, sofern nicht entfernt)',
          other: '# Komponenten fehlen im Scan (bleiben, sofern nicht entfernt)',
        })}`
      : null,
  ].filter((part): part is string => part !== null);
  const differences = differenceList.join(', ');
  const customer = target.instance.customer;
  const switchTo = target.switch_to !== null ? versionName(target.switch_to.version_nr, target.switch_to.custom) : '';
  const customVersionNr = target.custom_version_nr;
  const baseVersionNr = target.base_version.version_nr;

  return (
    <>
      <Banner kind="info" sub={differenceList.length > 0 ? t`Gegenüber ${current}: ${differences}.` : undefined}>
        {customer
          ? <Trans>Scan von <b>{instance}</b> ({customer}), läuft derzeit auf <b>{current}</b>.</Trans>
          : <Trans>Scan von <b>{instance}</b>, läuft derzeit auf <b>{current}</b>.</Trans>}
      </Banner>

      {target.outcome === 'identical' ? (
        <Banner kind="ok"><Trans>Die gescannte Konfiguration entspricht der aktuellen Version — die Version der Instanz bleibt.</Trans></Banner>
      ) : null}

      {target.outcome === 'switch' && target.switch_to !== null ? (
        <Banner kind="info" sub={t`Es wird keine neue Version angelegt.`}>
          <Trans>
            Die gescannte Konfiguration entspricht <b>{switchTo}</b> — die Instanz wird beim Übernehmen darauf
            umgestellt.
          </Trans>
        </Banner>
      ) : null}

      {target.outcome === 'deviation' ? (
        <section className="imp-target" aria-labelledby={`${idPrefix}-title`}>
          <h2 id={`${idPrefix}-title`}><Trans>Die Konfiguration weicht von allen Versionen ab</Trans></h2>
          <div className="seg imp-target-mode" role="radiogroup" aria-label={t`Neue Version`}>
            <button
              type="button"
              role="radio"
              aria-checked={mode === 'custom'}
              className={mode === 'custom' ? 'on' : undefined}
              disabled={disabled}
              onClick={() => setMode('custom')}
            >
              <Trans>Kundenversion anlegen</Trans>
            </button>
            <button
              type="button"
              role="radio"
              aria-checked={mode === 'official'}
              className={mode === 'official' ? 'on' : undefined}
              disabled={disabled}
              onClick={() => setMode('official')}
            >
              <Trans>Neue offizielle Version anlegen</Trans>
            </button>
          </div>
          {mode === 'custom' ? (
            <p className="imp-target-note">
              <Trans>
                Kennung <b>{customVersionNr}</b>, basiert auf Version {baseVersionNr} und gilt nur für diese
                Produktinstanz.
              </Trans>
            </p>
          ) : (
            <div className="imp-target-fields">
              <label htmlFor={`${idPrefix}-nr`}>
                <Trans>Versionsnummer</Trans>
                <input
                  id={`${idPrefix}-nr`}
                  type="text"
                  value={versionNr}
                  disabled={disabled}
                  required
                  aria-invalid={duplicate || undefined}
                  aria-describedby={duplicate ? `${idPrefix}-nr-error` : undefined}
                  onChange={(event) => setVersionNr(event.target.value)}
                  placeholder={t`z. B. 1.3`}
                />
              </label>
              {duplicate ? (
                <p id={`${idPrefix}-nr-error`} className="imp-target-error"><Trans>Version {trimmed} existiert bereits.</Trans></p>
              ) : null}
              <label htmlFor={`${idPrefix}-pred`}>
                <Trans>Vorgängerversion (optional)</Trans>
                <select
                  id={`${idPrefix}-pred`}
                  value={predecessorId ?? ''}
                  disabled={disabled}
                  onChange={(event) => setPredecessorId(event.target.value === '' ? null : Number(event.target.value))}
                >
                  <option value="">{t`Keine`}</option>
                  {versions.map((v) => <option key={v.id} value={v.id}>{v.version_nr}</option>)}
                </select>
              </label>
            </div>
          )}
          <p className="imp-target-note">
            <Trans>
              Ändern Sie unten Entscheidungen, prüft der Server beim Übernehmen erneut, ob eine gleiche Version
              bereits existiert, und stellt die Instanz dann darauf um.
            </Trans>
          </p>
        </section>
      ) : null}
    </>
  );
}

interface TargetSummaryProps {
  target: ProductImportTarget | InstanceImportTarget;
  disabled: boolean;
  onChoice: (choice: InstanceVersionChoice | null) => void;
}

export function TargetSummary({ target, ...props }: TargetSummaryProps): ReactNode {
  return target.kind === 'product'
    ? <ProductSummary target={target} />
    : <InstanceSummary target={target} {...props} />;
}
